import { evaluate7, combosOf, HANDS } from "./evaluator";

/**
 * All-in preflop equity.
 *
 * `exactEquity` enumerates every one of the C(48,5) = 1,712,304 boards for a given
 * pair of specific hole cards. It is the ground truth, and it is what the sampled
 * matrix is checked against.
 *
 * The full 169x169 matrix cannot be built exactly in reasonable time: roughly 14k
 * cells times several suit-isomorphism classes times a second each is over half a
 * day. So the matrix is sampled with a fixed seed — deterministic and reproducible —
 * and its error against exact enumeration is measured and recorded rather than
 * assumed. See `verifyMatrix`.
 */

/** Deterministic PRNG. A solve must be reproducible from its inputs alone. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Exact equity for two specific hole-card pairs. Hero's share of the pot, 0..1. */
export function exactEquity(a: [number, number], b: [number, number]): number {
  const dead = new Set([a[0], a[1], b[0], b[1]]);
  if (dead.size !== 4) throw new Error("overlapping hole cards");
  const deck: number[] = [];
  for (let c = 0; c < 52; c++) if (!dead.has(c)) deck.push(c);

  let win = 0, tie = 0, total = 0;
  const ha = [a[0], a[1], 0, 0, 0, 0, 0];
  const hb = [b[0], b[1], 0, 0, 0, 0, 0];
  const n = deck.length; // 48

  for (let i = 0; i < n - 4; i++) {
    ha[2] = hb[2] = deck[i]!;
    for (let j = i + 1; j < n - 3; j++) {
      ha[3] = hb[3] = deck[j]!;
      for (let k = j + 1; k < n - 2; k++) {
        ha[4] = hb[4] = deck[k]!;
        for (let l = k + 1; l < n - 1; l++) {
          ha[5] = hb[5] = deck[l]!;
          for (let m = l + 1; m < n; m++) {
            ha[6] = hb[6] = deck[m]!;
            const sa = evaluate7(ha), sb = evaluate7(hb);
            if (sa > sb) win++;
            else if (sa === sb) tie++;
            total++;
          }
        }
      }
    }
  }
  return (win + tie / 2) / total;
}

/**
 * Sampled equity for two canonical hand codes.
 *
 * Suit configurations are *enumerated*, not drawn at random: every valid combo pair
 * gets an equal share of the board samples. That removes suit-selection variance
 * entirely, leaving only board variance, and costs nothing extra. Drawing suits
 * randomly wastes samples re-discovering a distribution we already know exactly.
 */
export function sampledEquity(codeA: string, codeB: string, samples: number, rng: () => number): number {
  const ca = combosOf(codeA);
  const cb = combosOf(codeB);

  const pairs: [[number, number], [number, number]][] = [];
  for (const A of ca) for (const B of cb) {
    if (A[0] === B[0] || A[0] === B[1] || A[1] === B[0] || A[1] === B[1]) continue;
    pairs.push([A, B]);
  }
  if (!pairs.length) return 0.5;   // e.g. AA vs AA has no non-overlapping combos in this sense

  const per = Math.max(1, Math.round(samples / pairs.length));
  const hand = [0, 0, 0, 0, 0, 0, 0];
  const vill = [0, 0, 0, 0, 0, 0, 0];
  const used = new Uint8Array(52);

  let win = 0, tie = 0, n = 0;
  for (const [A, B] of pairs) {
    hand[0] = A[0]; hand[1] = A[1];
    vill[0] = B[0]; vill[1] = B[1];
    for (let s = 0; s < per; s++) {
      used.fill(0);
      used[A[0]] = used[A[1]] = used[B[0]] = used[B[1]] = 1;
      for (let d = 0; d < 5; d++) {
        let c: number;
        do { c = (rng() * 52) | 0; } while (used[c]);
        used[c] = 1;
        hand[2 + d] = c;
        vill[2 + d] = c;
      }
      const sa = evaluate7(hand), sb = evaluate7(vill);
      if (sa > sb) win++; else if (sa === sb) tie++;
      n++;
    }
  }
  return (win + tie / 2) / n;
}

export interface EquityMatrix {
  /** equity[i][j] = hand i's share against hand j. */
  m: Float64Array;
  samples: number;
  seed: number;
  builtAt: number;
}

export const idx = (i: number, j: number) => i * 169 + j;

/**
 * A hand against itself is exactly 0.5 — the two players hold the same two ranks and
 * the board is symmetric between them — so it is set, not sampled.
 *
 * The loop starts at j === i and writes both triangles, which for the diagonal means
 * the second write lands on the same cell as the first: the stored value was `1 - e`
 * rather than the sampled `e`, off by |1 - 2e| — about 0.0007 at the shipped sample
 * count. The published matrix has an exact 0.5 diagonal because it was corrected
 * after the fact; without this the code no longer reproduces its own output.
 */
export function buildMatrix(samples: number, seed = 20260827, onRow?: (i: number) => void): EquityMatrix {
  const m = new Float64Array(169 * 169);
  const rng = mulberry32(seed);
  for (let i = 0; i < 169; i++) {
  // The self-equity sample is still drawn, and discarded. It is not needed — a hand
  // against itself is exactly 0.5 — but the generator is shared across the row, so
  // skipping the draw would shift every cell after it and the shipped matrix would
  // stop being reproducible from this code.

    for (let j = i; j < 169; j++) {
      const e = sampledEquity(HANDS[i]!, HANDS[j]!, samples, rng);
      if (j === i) { m[idx(i, i)] = 0.5; continue; }
      m[idx(i, j)] = e;
      m[idx(j, i)] = 1 - e;
    }
    onRow?.(i);
  }
  return { m, samples, seed, builtAt: Math.floor(Date.now() / 1000) };
}
